import Typography from "@mui/material/Typography";
import React, {
  type ChangeEvent,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import CloseIcon from "@mui/icons-material/Close";
import ContrastIcon from "@mui/icons-material/Contrast";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import StraightenIcon from "@mui/icons-material/Straighten";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import Dialog from "@mui/material/Dialog";

import { HTTPMethod } from "@fusionauth/typescript-client";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
} from "@mui/material";
import Slider from "@mui/material/Slider";
import {
  Annotation,
  AnnotationGroupFactory,
  App,
  AppOptions,
  DataElement,
  DicomParser,
  DicomWriter,
  getUID,
  Index,
  ToolConfig,
  ViewConfig,
} from "dwv";
import { ProjectContext } from "../../../../contexts/ProjectContext.tsx";
import { projectsPrefix } from "../../../../services/ProjectsServices.ts";
import theme from "../../../../styles/theme.ts";
import { getDiffDaysMessage } from "../../../../utils/main.ts";
import CloseArrowTooltip from "../../../CloseArrowTooltip.tsx";
import TagsTable from "./TagsTable.jsx";

export type Metadata = {
  [x: string]: DataElement;
};

type DwvEvent = {
  source: string[];
  loadtype: string;
  dataid: string;
  type: string;
};

let initialized = false;
const saveFailedMessage =
  "Failed to save annotations. Please try again and contact support if the problem persists.";
export default function DwvViewer({
  open,
  onClose,
  dicomNoteData,
  radiologyImageInfo,
  categorize,
  patientNumber,
  patientId,
  visitId,
  visitNumber,
  visitTitle,
}: {
  open: boolean;
  onClose: () => void;
  dicomNoteData: ESDoc;
  radiologyImageInfo: DicomImageInfo;
  categorize: (e?: React.MouseEvent<HTMLElement>) => void;
  patientNumber: number;
  patientId: string;
  visitId: string;
  visitNumber: number;
  visitTitle: string;
}) {
  const { id: projectId, isLocked } = useContext(ProjectContext) || {};
  const [selectedTool, setSelectedTool] = useState("Scroll");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dwvApp, setDwvApp] = useState<App>();
  const [metadata, setMetadata] = useState<Metadata | undefined>({});
  const [orientation, setOrientation] = useState<string | undefined>(undefined);
  const [showDicomTags, setShowDicomTags] = useState(false);
  const [hasAnnotations, setHasAnnotations] = useState(false);
  const [annotationData, setAnnotationData] = useState<ArrayBuffer>();
  const [showAnnotations, setShowAnnotations] = useState(true);

  const tools = useMemo(() => {
    return {
      Scroll: new ToolConfig(),
      ZoomAndPan: new ToolConfig(),
      WindowLevel: new ToolConfig(),
      Draw: new ToolConfig(["Ruler"]),
    };
  }, []);

  function onChangeTool(tool: string) {
    if (dwvApp && tool) {
      setSelectedTool(tool);
      dwvApp.setTool(tool);
      if (tool === "Draw" && tools.Draw.options) {
        dwvApp.setToolFeatures({ shapeName: tools.Draw.options[0] });
      }
    }
  }

  const handleToolChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTool: string,
  ) => {
    if (newTool) {
      onChangeTool(newTool);
    }
  };

  function onReset() {
    if (dwvApp) {
      dwvApp.resetZoomPan();
    }
  }

  const toggleOrientation = () => {
    if (!dwvApp) return;
    let newOrientation = "coronal";
    if (typeof orientation !== "undefined") {
      if (orientation === "axial") {
        newOrientation = "coronal";
      } else if (orientation === "coronal") {
        newOrientation = "sagittal";
      } else if (orientation === "sagittal") {
        newOrientation = "axial";
      }
    }
    setOrientation(newOrientation);
    // update data view config
    const config = {
      "*": [
        {
          divId: "layerGroup0",
          orientation: newOrientation,
          colourMap: undefined,
          opacity: undefined,
          wlPresetName: undefined,
          windowCenter: undefined,
          windowWidth: undefined,
          fillOpacity: undefined,
          contourThickness: undefined,
        },
      ],
    };
    dwvApp.setDataViewConfigs(config);
    // render data
    const dataIds = dwvApp.getDataIds();
    for (const dataId of dataIds) {
      dwvApp.render(dataId);
    }
  };

  const handleClose = () => {
    initialized = false;
    onClose();
  };

  const handleSaveAndClose = async () => {
    if (dwvApp && dataLoaded) {
      if (
        !dwvApp.getActiveLayerGroup() ||
        !dwvApp.getActiveLayerGroup()?.getActiveDrawLayer()
      ) {
        handleClose();
      }

      const factory = new AnnotationGroupFactory();
      const sopUID = getUID("SOPInstanceUID");
      const extraTags = {
        MediaStorageSOPInstanceUID: sopUID,
        SOPInstanceUID: sopUID,
        InstanceNumber: 123,
        SeriesInstanceUID: getUID("SeriesInstanceUID"),
        SeriesNumber: 123,
        SeriesDescription: "Annotation made with dwv",
      };

      const dataId = dwvApp
        .getActiveLayerGroup()!
        .getActiveDrawLayer()!
        .getDataId();
      const annotationGroup = dwvApp.getData(dataId)?.annotationGroup;

      try {
        // write
        const writer = new DicomWriter();
        let dicomBuffer = undefined;
        if (!annotationGroup || annotationGroup.getLength() !== 0) {
          // this throws an error if there aren't any annotations
          const dicomElements = factory.toDicom(annotationGroup!, extraTags);
          dicomBuffer = writer.getBuffer(dicomElements);
        }
        const blob = new Blob(dicomBuffer ? [dicomBuffer] : undefined, {
          type: "application/dicom",
        });
        const data = new FormData();
        data.append("annotationFile", blob, "annotations.dcm");
        data.append("visitId", visitId);
        data.append("visitNumber", `${visitNumber}`);
        data.append("visitTitle", visitTitle);
        data.append("noteTitle", dicomNoteData.noteTitle);
        // call endpoint to save annotations
        fetch(
          `${projectsPrefix}/${projectId}/patients/${patientId}/dicom/${radiologyImageInfo.studyInstanceUid}/${radiologyImageInfo.seriesInstanceUid}/dicom/annotations`,
          {
            method: HTTPMethod.PUT,
            credentials: "include",
            body: data,
          },
        )
          .then((response) => {
            if (response.status !== 200) {
              console.error(response.body);
              alert(saveFailedMessage);
            } else {
              handleClose();
            }
          })
          .catch((error) => {
            console.error(error);
            alert(saveFailedMessage);
          });
      } catch (error) {
        console.error(error);
        alert(saveFailedMessage);
      }
    }
  };

  const handleLoadAnnotations = useCallback(() => {
    if (!dwvApp) return;
    const parser = new DicomParser();
    parser.parse(annotationData!);
    const factory = new AnnotationGroupFactory();
    const annotationGroup = factory.create(parser.getDicomElements());
    const dataId = dwvApp
      .getActiveLayerGroup()
      ?.getViewLayersFromActive()[0]
      .getDataId();
    if (dataId) {
      const annotations = dwvApp.createAnnotationData(dataId);
      annotations.annotationGroup = annotationGroup;
      dwvApp.addAndRenderAnnotationData(annotations, "layerGroup0", dataId);
      setHasAnnotations(true);
    }
  }, [dwvApp, annotationData]);

  const toggleAnnotationVisibility = () => {
    if (dwvApp && dwvApp.getActiveLayerGroup()) {
      const drawLayer = dwvApp.getActiveLayerGroup()!.getActiveDrawLayer();
      if (drawLayer) {
        const annotationGroup = dwvApp.getData(
          drawLayer.getDataId(),
        )!.annotationGroup;
        annotationGroup?.getList().forEach((annotation: Annotation) => {
          drawLayer.setAnnotationVisibility(
            annotation.trackingUid,
            !showAnnotations,
          );
        });
        setShowAnnotations(!showAnnotations);
      }
    }
  };

  const handleTagsDialogOpen = () => {
    setShowDicomTags(true);
  };

  const handleTagsDialogClose = () => {
    setShowDicomTags(false);
  };

  const [imageSliderValue, setImageSliderValue] = React.useState(1);
  const [imageSliderMax, setImageSliderMax] = React.useState(1);
  const handleSliderChange = (_event: Event, newValue: number) => {
    setImageSliderValue(newValue);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImageSliderValue(
      event.target.value === "" ? 0 : Number(event.target.value),
    );
  };

  const handleBlur = () => {
    if (imageSliderValue < 0) {
      setImageSliderValue(0);
    }
  };

  const toolsButtons = Object.keys(tools).map((tool) => {
    const details: { icon?: ReactElement; tooltip?: string } = {};
    switch (tool) {
      case "Scroll":
        details.icon = <MenuIcon />;
        details.tooltip = "Scroll through the image slices";
        break;
      case "WindowLevel":
        details.icon = <ContrastIcon />;
        details.tooltip = "Adjust image brightness and contrast";
        break;
      case "ZoomAndPan":
        details.icon = <SearchIcon />;
        details.tooltip = "Zoom into the image or move it around";
        break;
      default:
        details.icon = <StraightenIcon />;
        details.tooltip = "Draw measurement on the image";
    }
    return (
      <CloseArrowTooltip key={tool} title={details.tooltip}>
        <span>
          <ToggleButton value={tool} key={tool} disabled={!dataLoaded}>
            {details.icon}
          </ToggleButton>
        </span>
      </CloseArrowTooltip>
    );
  });

  useEffect(() => {
    if (dicomNoteData && !initialized) {
      initialized = true;
      // create the dwv app
      const app = new App();
      // initialize
      const viewConfig0 = new ViewConfig("layerGroup0");
      const viewConfigs = { "*": [viewConfig0] };
      const options = new AppOptions(viewConfigs);
      options.tools = tools;
      app.init(options);

      app.addEventListener("load", function (event: DwvEvent) {
        // set dicom tags
        setMetadata(app.getMetaData(event.dataid));
        // set data loaded flag
        setDataLoaded(true);
        // activate tool and update range max on load
        app.setTool("Scroll");
        const size = app.getData(event.dataid)!.image!.getGeometry().getSize();
        setImageSliderMax(size?.get(2));
        // update slider on slice change (for ex via mouse wheel)
        app.addEventListener("positionchange", function () {
          const vc = app!
            .getLayerGroupByDivId("layerGroup0")!
            .getBaseViewLayer()!
            .getViewController();
          setImageSliderValue((vc.getCurrentIndex().get(2) || 0) + 1);
        });
      });

      app.addEventListener("annotationadd", function () {
        setHasAnnotations(true);
      });
      app.addEventListener("annotationupdate", function () {
        setHasAnnotations(true);
      });
      app.addEventListener("annotationremove", function () {
        setHasAnnotations(true);
      });

      // load dicom data
      app.loadURLs(
        [
          `${projectsPrefix}/${projectId}/patients/dicom/${radiologyImageInfo.studyInstanceUid}/${radiologyImageInfo.seriesInstanceUid}/dicom.zip`,
        ],
        {
          method: "GET",
          requestHeaders: [
            {
              name: "Accept",
              value: "application/zip",
            },
            {
              name: "Content-Type",
              value: "application/zip",
            },
          ],
          withCredentials: true,
        },
      );
      setDwvApp(app);

      // fetch annotation data
      fetch(
        `${projectsPrefix}/${projectId}/patients/dicom/${radiologyImageInfo.studyInstanceUid}/${radiologyImageInfo.seriesInstanceUid}/annotations.dcm`,
        {
          method: HTTPMethod.GET,
          headers: {
            Accept: "*/*",
          },
          credentials: "include",
        },
      ).then((response) => {
        if (response.status === 200) {
          response.arrayBuffer().then((d) => {
            setAnnotationData(d);
          });
        } else {
          console.debug("No annotation data.");
        }
      });
    }
  }, [
    tools,
    dicomNoteData,
    imageSliderValue,
    projectId,
    radiologyImageInfo.seriesInstanceUid,
    radiologyImageInfo.studyInstanceUid,
  ]);

  useEffect(() => {
    // handles resizing the dicom image if the window is resized
    if (dwvApp && initialized) {
      const handleResize = () => {
        if (dwvApp) {
          dwvApp.fitToContainer();
        }
      };
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [dwvApp]);

  useEffect(() => {
    if (dwvApp && dataLoaded) {
      // update app on slider or slider input change
      const vc = dwvApp
        .getLayerGroupByDivId("layerGroup0")!
        .getBaseViewLayer()!
        .getViewController();
      const values = vc.getCurrentIndex().getValues();
      values[2] = imageSliderValue - 1;
      vc.setCurrentIndex(new Index(values));
    }
  }, [dataLoaded, dwvApp, imageSliderValue]);

  useEffect(() => {
    if (dwvApp && dataLoaded && !hasAnnotations && annotationData) {
      // load existing annotations only once
      handleLoadAnnotations();
    }
  }, [
    annotationData,
    dataLoaded,
    dwvApp,
    handleLoadAnnotations,
    hasAnnotations,
  ]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "unset",
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          paddingBottom: 0,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6">
            Patient {patientNumber} /{" "}
            {getDiffDaysMessage(dicomNoteData.diffDays)} /{" "}
            {dicomNoteData.noteTitle}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{ color: theme.palette.text.secondary }}
          >
            <Typography variant="body2">
              <b>Modality:</b> {dicomNoteData.modality || "No data available"}
            </Typography>
            <Typography variant="body2">
              <b>Series Description:</b>{" "}
              {radiologyImageInfo.seriesDescription || "No data available"}
            </Typography>
            <Typography variant="body2">
              <b>Study Description:</b>{" "}
              {radiologyImageInfo.studyDescription || "No data available"}
            </Typography>
            <Typography variant="body2">
              <b>Body Part Examined:</b>{" "}
              {dicomNoteData.bodyPartExamined || "No data available"}
            </Typography>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          spacing={1}
          padding={1}
          justifyContent="center"
          sx={{ flex: "none" }}
        >
          <Stack
            direction="row"
            sx={{
              width: 310,
              alignItems: "center",
              border: "1px solid rgb(0,0,0,0.12)",
              borderRadius: "4px",
            }}
          >
            <Input
              sx={{ width: 45, margin: "7px", marginRight: "15px" }}
              value={imageSliderValue}
              size={"small"}
              onChange={handleInputChange}
              onBlur={handleBlur}
              inputProps={{
                step: 1,
                min: 1,
                max: imageSliderMax,
                type: "number",
              }}
            />
            <Slider
              value={
                typeof imageSliderValue === "number" ? imageSliderValue : 1
              }
              onChange={handleSliderChange}
              marks={Array.from({ length: imageSliderMax }, (_, i) => {
                const val = i + 1;
                return {
                  value: val,
                  label: val === 1 || val === imageSliderMax ? val : "",
                };
              })}
              step={1}
              min={1}
              max={imageSliderMax}
              valueLabelDisplay={"auto"}
              valueLabelFormat={(value) => `Image slice ${value}`}
              sx={{
                width: 250,
                marginY: "7px",
                marginLeft: 0,
                marginRight: "12px",
                "& .MuiSlider-markLabel": {
                  // Styles for mark labels
                  fontSize: "0.75rem",
                  lineHeight: "0",
                },
                "& .MuiSlider-thumb": {
                  // styles for thumb (dot)
                  height: "16px",
                  width: "16px",
                },
                "& .MuiSlider-valueLabel": {
                  //styles for value hover label
                  fontSize: "0.75rem",
                  lineHeight: "1rem",
                  top: 0,
                },
              }}
            />
          </Stack>
          <ToggleButtonGroup
            size="small"
            color="primary"
            value={selectedTool}
            exclusive
            onChange={handleToolChange}
          >
            {toolsButtons}
          </ToggleButtonGroup>

          <CloseArrowTooltip title={"Show or hide annotations"}>
            <span>
              <ToggleButton
                size="small"
                value="showHide"
                disabled={!dataLoaded || !hasAnnotations}
                onChange={toggleAnnotationVisibility}
              >
                {showAnnotations ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </ToggleButton>
            </span>
          </CloseArrowTooltip>

          <CloseArrowTooltip title={"Reset the image size and position"}>
            <span>
              <ToggleButton
                size="small"
                value="reset"
                disabled={!dataLoaded}
                onChange={onReset}
              >
                <RefreshIcon />
              </ToggleButton>
            </span>
          </CloseArrowTooltip>

          <CloseArrowTooltip title={"Toggle the image orientation"}>
            <span>
              <ToggleButton
                size="small"
                value="toggleOrientation"
                disabled={!dataLoaded}
                onClick={toggleOrientation}
              >
                <CameraswitchIcon />
              </ToggleButton>
            </span>
          </CloseArrowTooltip>

          <CloseArrowTooltip title={"Show the image DICOM tags"}>
            <span>
              <ToggleButton
                size="small"
                value="tags"
                disabled={!dataLoaded}
                onClick={handleTagsDialogOpen}
              >
                <LibraryBooksIcon />
              </ToggleButton>
            </span>
          </CloseArrowTooltip>

          <Dialog
            open={showDicomTags}
            onClose={handleTagsDialogClose}
            scroll="body"
          >
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", padding: "16px" }}
            >
              <Typography variant="h6" color="inherit">
                DICOM Tags
              </Typography>
              <IconButton
                color="inherit"
                onClick={handleTagsDialogClose}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <TagsTable fullMetaData={metadata} />
          </Dialog>
        </Stack>
        <div id="layerGroup0" className="layerGroup">
          {!dataLoaded && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress color={"secondary"} size="1.5rem" />
            </Box>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <CloseArrowTooltip
          title={
            isLocked || !hasAnnotations
              ? ""
              : "Discard changes to annotations and close image viewer."
          }
        >
          <span>
            <Button size="small" variant="outlined" onClick={handleClose}>
              {isLocked || !hasAnnotations ? "Close" : "Discard & Close"}
            </Button>
          </span>
        </CloseArrowTooltip>
        {!isLocked && hasAnnotations && (
          <CloseArrowTooltip
            title={"Save changes to annotations and close image viewer."}
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveAndClose}
              >
                Save & Close
              </Button>
            </span>
          </CloseArrowTooltip>
        )}
        <CloseArrowTooltip title={isLocked ? "This project is locked." : ""}>
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={isLocked}
              onClick={categorize}
            >
              Categorize Patient
            </Button>
          </span>
        </CloseArrowTooltip>
      </DialogActions>
    </Dialog>
  );
}
